import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from '../components/DataTable';
import { getAllInquiries, updateInquiryStatus, deleteInquiry } from '../redux/slice/inquiry.slice';
import ViewInquiryModal from '../components/ViewInquiryModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import {
    Eye, Trash2, ChevronDown, Download, FileText, Search, Filter,
    MessageSquare, CheckCircle, Clock, AlertCircle, Check
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExportDateRangeModal from '../components/ExportDateRangeModal';

const Inquiries = () => {
    const dispatch = useDispatch();
    const { inquiries, loading, error } = useSelector((state) => state.inquiry);

    // Local State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Modal States
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [inquiryToDelete, setInquiryToDelete] = useState(null);

    // Export States
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportType, setExportType] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowExportDropdown(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        dispatch(getAllInquiries());
    }, [dispatch]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, statusFilter]);

    // Statistics Calculation
    const stats = {
        total: inquiries?.length,
        new: inquiries?.filter(i => i.status === 'New')?.length,
        inProgress: inquiries?.filter(i => i.status === 'In Progress')?.length,
        resolved: inquiries?.filter(i => i.status === 'Resolved')?.length
    };

    // Filter Logic
    const filteredInquiries = inquiries?.filter(inq => {
        const matchesSearch =
            (inq.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (inq.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (inq.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (inq.phone || '').includes(searchTerm);

        const matchesStatus = statusFilter === 'All' || inq.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const paginatedInquiries = filteredInquiries?.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleView = (inquiry) => {
        setSelectedInquiry(inquiry);
        setViewModalOpen(true);
    };

    const handleDelete = (id) => {
        setInquiryToDelete(id);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (inquiryToDelete) {
            dispatch(deleteInquiry(inquiryToDelete));
            setDeleteModalOpen(false);
            setInquiryToDelete(null);
        }
    };

    const handleStatusChange = (id, newStatus) => {
        dispatch(updateInquiryStatus({ id, status: newStatus }));
    };

    const handleExportClick = (type) => {
        setExportType(type);
        setExportModalOpen(true);
        setShowExportDropdown(false);
    };

    const handleExport = (startDate, endDate) => {
        // ... (existing export logic logic reused but filtering on *current filtered data* could be better, keeping original logic for date range export)
        const filterInquiriesByDate = (start, end) => {
            const sDate = new Date(start); sDate.setHours(0, 0, 0, 0);
            const eDate = new Date(end); eDate.setHours(23, 59, 59, 999);
            return inquiries.filter(inq => {
                const inqDate = new Date(inq.createdAt);
                return inqDate >= sDate && inqDate <= eDate;
            });
        };

        const dataToExport = filterInquiriesByDate(startDate, endDate);

        if (dataToExport.length === 0) {
            alert('No data found for the selected date range.');
            return;
        }

        if (exportType === 'Excel') {
            exportToExcel(dataToExport, startDate, endDate);
        } else {
            exportToPDF(dataToExport, startDate, endDate);
        }
    };

    const exportToExcel = (data, startDate, endDate) => {
        const dataToExport = data.map(inquiry => ({
            'Name': `${inquiry.firstName} ${inquiry.lastName || ''}`,
            'Email': inquiry.email,
            'Phone': inquiry.phone,
            'Interested In': inquiry.project ? inquiry.project.title : 'General Inquiry',
            'Status': inquiry.status,
            'Message': inquiry.message,
            'Date': new Date(inquiry.createdAt).toLocaleString()
        }));
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Inquiries");
        XLSX.writeFile(workbook, `inquiries_${startDate}_to_${endDate}.xlsx`);
    };

    const exportToPDF = (data, startDate, endDate) => {
        const doc = new jsPDF();
        doc.text("Inquiries Report", 14, 15);
        doc.setFontSize(10);
        doc.text(`Range: ${startDate} to ${endDate}`, 14, 22);

        const tableColumn = ["Name", "Email", "Phone", "Interested In", "Status", "Date"];
        const tableRows = data.map(inquiry => [
            `${inquiry.firstName} ${inquiry.lastName || ''}`,
            inquiry.email,
            inquiry.phone,
            inquiry.project ? inquiry.project.title : 'General Inquiry',
            inquiry.status,
            new Date(inquiry.createdAt).toLocaleDateString()
        ]);

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 35 });
        doc.save(`inquiries_${startDate}_to_${endDate}.pdf`);
    };

    const statusColors = {
        'New': 'bg-blue-100 text-blue-800 border-blue-200',
        'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-200',
        'Resolved': 'bg-green-100 text-green-800 border-green-200',
    };

    const columns = [
        {
            header: 'User Details',
            accessor: 'firstName',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-gray-900">{row.firstName} {row.lastName}</span>
                    <a href={`mailto:${row.email}`} className="text-xs text-gray-500 hover:text-blue-600 truncate max-w-[150px]">{row.email}</a>
                </div>
            )
        },
        { header: 'Phone', accessor: 'phone', className: " md:table-cell" },
        {
            header: 'Status',
            accessor: 'status',
            render: (row) => (
                <StatusSelect
                    currentStatus={row.status}
                    onStatusChange={(newStatus) => handleStatusChange(row._id, newStatus)}
                    statusColors={statusColors}
                />
            )
        },
        {
            header: 'Project',
            accessor: 'project',
            className: "lg:table-cell",
            render: (row) => (
                <span className="text-sm font-medium text-gray-700 truncate max-w-[150px] block">
                    {row.project ? row.projectName : <span className="text-gray-400 italic">General</span>}
                </span>
            )
        },
        {
            header: 'Date',
            accessor: 'createdAt',
            className: "sm:table-cell",
            render: (row) => (
                <div className="text-sm text-gray-500 whitespace-nowrap">
                    {new Date(row.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
            )
        }
    ];

    if (loading) return (
        <div className="min-h-[400px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
    );

    if (error) return (
        <div className="p-6 text-red-500 bg-red-50 rounded-xl border border-red-100 m-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>Error: {error}</span>
        </div>
    );

    return (
        <div className="max-w-[95%] mx-auto py-8">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-serif font-bold text-gray-900">Inquiries</h2>
                    <p className="text-gray-500 mt-1">Manage and track user inquiries</p>
                </div>

                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setShowExportDropdown(!showExportDropdown)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all shadow-md hover:shadow-lg font-medium text-sm"
                    >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Data</span>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showExportDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showExportDropdown && (
                        <div className="absolute left-0 lg:left-auto lg:right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20 animate-fade-in-up">
                            <button
                                onClick={() => handleExportClick('Excel')}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors"
                            >
                                <div className="p-1.5 bg-green-50 rounded text-green-600">
                                    <FileText className="w-4 h-4" />
                                </div>
                                <span className="font-medium text-sm">Export to Excel</span>
                            </button>
                            <button
                                onClick={() => handleExportClick('PDF')}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors border-t border-gray-50"
                            >
                                <div className="p-1.5 bg-red-50 rounded text-red-600">
                                    <FileText className="w-4 h-4" />
                                </div>
                                <span className="font-medium text-sm">Export to PDF</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard
                    title="Total Inquiries"
                    value={stats.total}
                    icon={MessageSquare}
                    color="bg-blue-50 text-blue-600"
                />
                <StatCard
                    title="New"
                    value={stats.new}
                    icon={AlertCircle}
                    color="bg-purple-50 text-purple-600"
                />
                <StatCard
                    title="In Progress"
                    value={stats.inProgress}
                    icon={Clock}
                    color="bg-yellow-50 text-yellow-600"
                />
                <StatCard
                    title="Resolved"
                    value={stats.resolved}
                    icon={CheckCircle}
                    color="bg-green-50 text-green-600"
                />
            </div>

            {/* Search and Filter */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col lg:flex-row gap-4 justify-between items-center">
                <div className="relative w-full lg:max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search users, emails..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm transition-all"
                    />
                </div>

                <div className="relative w-full lg:w-auto" ref={statusDropdownRef}>
                    <button
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        className="w-full lg:w-48 flex items-center justify-between px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors shadow-sm"
                    >
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-gray-500" />
                            <span>{statusFilter === 'All' ? 'All Status' : statusFilter}</span>
                        </div>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showStatusDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    {showStatusDropdown && (
                        <div className="absolute right-0 top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20 animate-fade-in-up">
                            {['All', 'New', 'In Progress', 'Resolved'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => {
                                        setStatusFilter(status);
                                        setShowStatusDropdown(false);
                                    }}
                                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between text-sm transition-colors ${statusFilter === status ? 'text-blue-600 bg-blue-50/50 font-medium' : 'text-gray-700'
                                        }`}
                                >
                                    <span>{status === 'All' ? 'All Status' : status}</span>
                                    {statusFilter === status && <Check className="w-4 h-4" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Data Table */}
            <DataTable
                columns={columns}
                data={paginatedInquiries}
                onEdit={() => { }}
                onDelete={(id) => handleDelete(id)}
                onView={(id) => handleView(inquiries.find(i => i._id === id))}
                showEdit={false}
                pagination={true}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredInquiries?.length || 0}
                onPageChange={handlePageChange}
            />

            {/* Modals */}
            <ViewInquiryModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                inquiry={selectedInquiry}
            />

            <ExportDateRangeModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExport}
                type={exportType}
            />

            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Inquiry"
                message="Are you sure you want to delete this inquiry? This action cannot be undone."
            />
        </div>
    );
};

const StatusSelect = ({ currentStatus, onStatusChange, statusColors }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                buttonRef.current &&
                !buttonRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            const updatePosition = () => {
                if (buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    setCoords({
                        top: rect.bottom + window.scrollY + 4,
                        left: rect.left + window.scrollX
                    });
                }
            };

            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', () => setIsOpen(false), true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', () => setIsOpen(false), true);
            };
        }
    }, [isOpen]);

    return (
        <>
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 border transition-all ${statusColors[currentStatus] || 'bg-gray-100 border-gray-200'}`}
            >
                <span className="whitespace-nowrap">{currentStatus}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: coords.top,
                        left: coords.left,
                        zIndex: 9999,
                        minWidth: '140px'
                    }}
                    className="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden animate-fade-in-up"
                >
                    {['New', 'In Progress', 'Resolved'].map((status) => (
                        <button
                            key={status}
                            onClick={() => {
                                onStatusChange(status);
                                setIsOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between text-xs transition-colors ${currentStatus === status ? 'text-blue-600 bg-blue-50/50 font-medium' : 'text-gray-700'
                                }`}
                        >
                            <span>{status}</span>
                            {currentStatus === status && <Check className="w-3 h-3" />}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4 transition-transform hover:scale-[1.02]">
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6" />
        </div>
        <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
    </div>
);

export default Inquiries;
